import React, {
  useState,
  useContext,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import {
  ModelContext,
  collectionMap,
} from '../../../Model/ModelContext/ModelContext';
import BreadcrumbsWrapper from '../../Delsystem/_Common/Components/BreadcrumbsWrapper/BreadcrumbsWrapper';
import { RutinSettingsContext } from '../../Delsystem/_Common/Components/RutinAvailabilityContext/RutinAvailabilityContext';
import { rutinInfos } from '../../Delsystem/_Common/Const/RutinInfo';
import { findCollection } from '../../../Model/avenyModelUtils';
import { VisningsTypContextProvider } from '../../Delsystem/_Common/Components/BusinessCollectionComponents/VisningsTypContext/VisningsTypContext';
import { DetaljurvalContextProvider } from '../../Delsystem/_Common/Components/BusinessCollectionComponents/DetaljUrvalContext/DetaljUrvalContext';
import DebugView from './DebugView';
import { makeStyles } from 'tss-react/mui';
import { useIsExtraSmallScreen } from '../../../Hooks/useFullscreenView';
import { IRutinInfo } from '../../../Types/RutinInfoType';
import { EDAutoCompleteEdit } from '../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import { identity, sortBy } from 'ramda';
import { StandardRutinContext } from '../../Delsystem/_StandardRutin/StandardRutinContext';
import { IUIUrval } from '../../../Types/UrvalTypes';
import defaultColumns from '../../Delsystem/_Common/Utils/defaultColumns';
import { IBusinessDataFormContext } from '../../Delsystem/_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import ErrorBoundary from '../../../Components/ErrorBoundary/ErrorBoundary';
import EDView from '../../../BaseComponents/View/EDView';
import { ActiveRoutineContextProvider } from '../../Routing/ActiveRoutine/ActiveRoutineContext';
import businessCollectionComponents from '../../Delsystem/_Common/Components/BusinessCollectionComponents/businessCollectionComponents';
import { useActiveDBCollectionQuery } from '../../../Components/QueryComponents/ActiveDBCollectionQuery/ActiveDBCollectionQuery';
import { addAllDataDependencies } from '../../Delsystem/_Common/Const/BusinessConfigs';
import { StandardRutinCollectionContextProvider } from '../../Delsystem/_StandardRutin/StandardRutinCollectionContext';
import {
  ISortInfo,
  ISortProperty,
  sortRows,
  standardRutinCompare,
  standardRutinGetSortValue,
} from '../../../BaseComponents/Table/Components/SortableTableUtils';
import { ICollectionDataType } from '../../Delsystem/_Common/Components/BusinessCollectionComponents/Queries/BusinessCollectionQuery';
import { ICurrentSorting } from '../../Delsystem/_StandardRutin/StandardRutinMaster';
import { getDefaultSortProperties } from '../../Delsystem/_StandardRutin/StandardRutinUtils';
import { filterData } from '../../Delsystem/_Common/Components/ListFilteringComponent/Utils/listFilterComponentUtils';
import { rutiner } from '../../Delsystem/_Common/Const/Rutiner';
import { useNavigate, useParams } from 'react-router';
import {
  buildBasePath,
  useBaseRouteValue,
} from '../../Routing/Utils/routerUtils';
const useStyles = makeStyles()(
  theme =>
    ({
      container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      },
      largeContainer: {
        paddingLeft: 62,
        paddingRight: 62,
        width: 'calc(100% - 124px)',
        flexGrow: 1,
      },
      select: {
        position: 'relative',
        top: 60,
        marginLeft: 140,
        width: 'calc(100% - 140px)',
      },
      labelFocused: {
        borderColor: theme.palette.primary.main,
      },
    } as const)
);

interface IProps {
  setDebugParams: (params: string[]) => void;
}

export const DebugRutinViewRoute: React.FC = () => {
  const { collectionName, detaljVy, rutinKod } = useParams();
  const navigate = useNavigate();
  const baseRouteValues = useBaseRouteValue();

  const setDebugParams = useCallback(
    (params: string[]) => {
      navigate(
        `${buildBasePath(baseRouteValues)}/debugRutin/${params.join('/')}`,
        { replace: true }
      );
    },
    [baseRouteValues, navigate]
  );

  return (
    <DebugRutinView
      key={`${collectionName}/${detaljVy}/${rutinKod}`}
      setDebugParams={setDebugParams}
    />
  );
};

/** Tom lista av rader */
const noRows: ICollectionDataType[] = [];

const DebugRutinView: React.FC<React.PropsWithChildren<IProps>> = props => {
  const { setDebugParams } = props;
  const { collectionName, detaljVy, rutinKod } = useParams() as {
    collectionName: string;
    detaljVy: string;
    rutinKod: string;
  };
  const rutin: IRutinInfo = rutinInfos[rutinKod];
  const setRutin = (rutinKod: string) =>
    props.setDebugParams([collectionName, detaljVy, rutinKod]);
  const { classes, cx } = useStyles();
  const isSmall = useIsExtraSmallScreen();
  const collection = findCollection(collectionMap, collectionName);
  const { setActiveRoutine } = useContext(RutinSettingsContext);
  const collectionOptions = useMemo(
    () => Array.from(collectionMap, ([_, coll]) => coll.CollectionName),
    []
  );
  const detaljVyOptions = useMemo(
    () =>
      businessCollectionComponents(collectionName)
        .map(comp => comp.id || 'utan id')
        .concat('debug'),
    [collectionName]
  );
  const rutinInfoOptions = Object.keys(rutinInfos).map(x => {
    const rutin: IRutinInfo = rutinInfos[x];
    return {
      name: rutin.benamning,
      value: x,
    };
  });

  //samma som setCollectionName, men Typescript tyckte inte typerna var kompatibla
  const changeCollectionName = useCallback(
    (option: string | null) => {
      if (option) {
        setDebugParams([option, detaljVy, rutinKod]);
      }
    },
    [detaljVy, setDebugParams, rutinKod]
  );

  const changeDetaljVy = useCallback(
    (option: string | null) => {
      if (option) {
        setDebugParams([collectionName, option, rutinKod]);
      }
    },
    [collectionName, setDebugParams, rutinKod]
  );
  const modelContext = React.useContext(ModelContext);
  const formStateRef = useRef<IBusinessDataFormContext<any> | null>(null);
  const [urval, setUrval] = useState<IUIUrval | null>(null);
  const [searchText, setSearchText] = useState('');
  const [columns, setColumns] = useState(() =>
    defaultColumns(collectionName, rutinKod)
  );

  const currentSortRef = useRef<ICurrentSorting>({});
  let filterAttributes = rutiner[rutinKod]?.filterAttributes;
  const attributeDefs = useMemo(
    () => addAllDataDependencies(collectionName, columns),
    [columns, collectionName]
  );
  const collectionQueryResult = useActiveDBCollectionQuery({
    collectionName: collectionName,
    queryName: `${rutinKod ?? 'ingenRutinkod'}Main`,
    attributeDefs: attributeDefs,
    urval: urval,
    // routineID: rutinID,
    networkOnlyOnFirst: true,
  });

  /**
   * Sorteringsinställningar. Raderna ska sorteras efter detta när inställningarna ändras eller urvalet laddas om.
   */
  const [sortProperties, setSortProperties] = useState<ISortProperty[]>(
    getDefaultSortProperties(columns, collectionName, modelContext, rutinKod)
  );

  const sortInfo = useMemo<ISortInfo<ICollectionDataType>>(() => {
    const lastSortInfo = currentSortRef.current;
    let lastSortOrder = lastSortInfo?.sortOrder;
    const normalizedRows = (collectionQueryResult.data || []).map(row => ({
      ...row,
      _Associations: row._Associations ?? null,
    }));
    const keepSortOrder =
      lastSortOrder &&
      lastSortInfo.properties === sortProperties &&
      !collectionQueryResult.loading;
    const rows = collectionQueryResult.loading
      ? noRows
      : keepSortOrder
      ? sortBy(row => lastSortInfo.sortOrder?.get(row.id) ?? 0, normalizedRows)
      : sortRows(
          sortProperties,
          normalizedRows,
          standardRutinGetSortValue(collectionName),
          standardRutinCompare
        );
    if (!keepSortOrder) {
      lastSortInfo.sortOrder = collectionQueryResult.loading
        ? undefined
        : new Map(rows.map((row, idx) => [row.id, idx]));
      lastSortInfo.properties = sortProperties;
    }
    return {
      rows: rows,
      properties: sortProperties,
    };
  }, [
    collectionName,
    collectionQueryResult.loading,
    collectionQueryResult.data,
    sortProperties,
  ]);

  const hasUrval = Boolean(urval);

  const filteredSortInfo = useMemo(
    () =>
      filterData(
        collectionName,
        sortInfo,
        searchText,
        filterAttributes,
        hasUrval,
        collectionQueryResult.loading
      ),
    [
      sortInfo,
      searchText,
      filterAttributes,
      hasUrval,
      collectionQueryResult.loading,
      collectionName,
    ]
  );

  const updateSortInfo = useCallback((info: ISortInfo<ICollectionDataType>) => {
    setSortProperties(info.properties);
  }, []);
  return (
    <div
      className={cx(classes.container, {
        [classes.largeContainer]: !isSmall,
      })}
    >
      <EDView
        style={{
          marginTop: '50px',
          display: 'flex',
          width: 1200,
        }}
      >
        <EDAutoCompleteEdit<string>
          value={collectionName}
          options={collectionOptions}
          onChange={changeCollectionName}
          getOptionLabel={identity}
          label="Collection"
          maxResult={5}
          fullWidth
        />
        <EDAutoCompleteEdit<string>
          value={detaljVy}
          options={detaljVyOptions}
          onChange={changeDetaljVy}
          getOptionLabel={identity}
          label="Detaljvy"
          maxResult={5}
          fullWidth
        />
        <EDAutoCompleteEdit
          value={rutinInfoOptions.find(x => x.value === rutinKod)}
          options={rutinInfoOptions}
          onChange={(option: { name: string; value: string } | null) => {
            if (!option) {
              return;
            }
            const rutin: IRutinInfo = rutinInfos[option.value];
            if (rutin) {
              setRutin(rutin.rutinKod);
              setActiveRoutine(rutin.rutinID);
            }
          }}
          label="Rutin"
          getOptionLabel={(option: { name: string; value: string }) =>
            `${option.name} (${option.value})`
          }
          fullWidth
        />
      </EDView>
      {rutin == null ? (
        `rutinkoden ${rutinKod} saknas`
      ) : (
        <StandardRutinCollectionContextProvider
          sortInfo={sortInfo}
          filteredSortInfo={filteredSortInfo}
          updateSortInfo={updateSortInfo}
          collectionQueryResult={collectionQueryResult}
          searchText={searchText}
          setSearchText={setSearchText}
          filterAttributes={filterAttributes}
        >
          <BreadcrumbsWrapper>
            <ActiveRoutineContextProvider
              rutinInfo={rutin}
              collectionName={collectionName}
              key={`${rutin.rutinID}-${collectionName}`}
            >
              <VisningsTypContextProvider
                rutinID={rutin.rutinID}
                collectionName={collectionName}
              >
                <DetaljurvalContextProvider
                  rutinID={rutin.rutinID}
                  collectionName={collectionName}
                  key={collectionName}
                >
                  <StandardRutinContext.Provider
                    value={{
                      rutinID: rutin.rutinID,
                      collectionName: collectionName,
                      urval,
                      setUrval,
                      columns,
                      setColumns,
                      formStateRef,
                      setOpenChoiceDialog: () => {},
                      refetchButtonQueries: [],
                      addRefetchButtonQuery: () => {},
                      selectNextDetail: React.createRef(),
                    }}
                  >
                    {collection && (
                      <ErrorBoundary
                        name={`DebugView för ${rutin?.rutinKod} och ${collection?.CollectionName}`}
                      >
                        <DebugView
                          rutinKod={rutin.rutinKod}
                          collection={collection}
                          key={collection.CollectionName}
                          detaljVy={detaljVy}
                        />
                      </ErrorBoundary>
                    )}
                  </StandardRutinContext.Provider>
                </DetaljurvalContextProvider>
              </VisningsTypContextProvider>
            </ActiveRoutineContextProvider>
          </BreadcrumbsWrapper>
        </StandardRutinCollectionContextProvider>
      )}
    </div>
  );
};
